import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import {
  formControlChanges$,
  isDisabled$,
  LoaderButtonComponent,
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  getMohCodeExtendedData,
  IMohPreApprovalData,
  IMohPreApprovalFundingRequest,
  IPatient,
  IPractice,
  IPricedServiceCodeEntry,
  IStaffer,
  ITreatmentStep,
  MOH_CLAIM_TYPE_LABEL_MAP,
  MohClaimType,
  MohGenerateFormRequest,
  MohHighCariesProviderData,
  MohHighCariesRequestReason,
  requiresMohPreApproval,
  ServiceCodeType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  Firestore,
  getEnumValues,
  getError,
  httpsCallable,
  ISODateType,
  serialise,
  SerialisedData,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { countBy, first, orderBy, toPairs, uniq } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  IMohAgreementHolderForm,
  MohAgreementHolderForm,
} from '../../../moh/moh-agreement-holder-form';
import { MohPreApprovalService } from '../../../moh/moh-pre-approval.service';
import { MohAgreementHolderFormComponent } from '../moh-agreement-holder-form/moh-agreement-holder-form.component';
import { MohTreatmentListCardComponent } from '../moh-treatment-list-card/moh-treatment-list-card.component';
import { MohHighCariesRequestFormComponent } from './moh-high-caries-request-form/moh-high-caries-request-form.component';

export interface IMohSendPreApprovalDialogData {
  step: WithRef<ITreatmentStep>;
  practice: WithRef<IPractice>;
  patient: WithRef<IPatient>;
  practitioner: WithRef<IStaffer>;
}

export type MohPreApprovalRequestForm = Pick<
  IMohPreApprovalData,
  'claimType' | 'comments' | 'nhiNumber'
> & {
  agreementHolder: IMohAgreementHolderForm;
  highCariesData?: MohHighCariesProviderData;
};

@Component({
  selector: 'pr-moh-send-pre-approval-dialog',
  imports: [
    NgDialogModule,
    MatToolbarModule,
    LoaderButtonComponent,
    MohAgreementHolderFormComponent,
    MohHighCariesRequestFormComponent,
    MohTreatmentListCardComponent,
  ],
  templateUrl: './moh-send-pre-approval-dialog.component.html',
  styleUrl: './moh-send-pre-approval-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MohSendPreApprovalDialogComponent {
  private _preApprovalCodes: IPricedServiceCodeEntry[];
  private _preApprovalClaimTypes: MohClaimType[];
  private _allClaimTypes: MohClaimType[];
  private _dialogRef = inject(MatDialogRef);
  private _approvalService = inject(MohPreApprovalService);
  data = inject<IMohSendPreApprovalDialogData>(MAT_DIALOG_DATA);
  claimTypes = getEnumValues(MohClaimType);
  filteredCodes$: Observable<IPricedServiceCodeEntry[]>;
  submitDisabled$: Observable<boolean>;
  isHighCaries$: Observable<boolean>;
  loading = signal(false);

  highCariesForm = new TypedFormGroup<MohHighCariesProviderData>({
    requestReason: new TypedFormControl<MohHighCariesRequestReason>(
      MohHighCariesRequestReason.YearNine
    ),
    dentinalToothStructure: new TypedFormControl<boolean>(false),
    teethBeenFissureSealed: new TypedFormControl<boolean>(false),
    teethRestored: new TypedFormControl<boolean>(false),
    notFissureSealedReason: new TypedFormControl<string>(),
    dateFissureSealed: new TypedFormControl<ISODateType>(),
    dateTeethRestored: new TypedFormControl<string>(),
    otherComments: new TypedFormControl<string>(),
  });
  agreementHolderForm = MohAgreementHolderForm.init();
  form = new TypedFormGroup<MohPreApprovalRequestForm>({
    claimType: new TypedFormControl<MohClaimType>(),
    agreementHolder: this.agreementHolderForm,
    comments: new TypedFormControl<string>(''),
    nhiNumber: new TypedFormControl<string>('', Validators.maxLength(7)),
    highCariesData: this.highCariesForm,
  });

  constructor() {
    this._preApprovalCodes = this.data.step.treatments
      .flatMap((treatment) => treatment.serviceCodes)
      .filter(
        (code) =>
          code.type === ServiceCodeType.MOH && requiresMohPreApproval(code)
      );

    this._preApprovalClaimTypes = this._preApprovalCodes.flatMap(
      (code) => getMohCodeExtendedData(code.code)?.preApprovalClaimTypes ?? []
    );
    this._allClaimTypes = uniq(this._preApprovalClaimTypes);

    this.form.patchValue({
      nhiNumber: this.data.patient.nationalHealthIndexNumber,
      claimType: this._inferClaimType(),
    });

    this.filteredCodes$ = formControlChanges$(
      this.form.controls.claimType
    ).pipe(
      filterUndefined(),
      map((claimType) =>
        this._preApprovalCodes.filter((code) =>
          requiresMohPreApproval(code, claimType)
        )
      )
    );

    this.submitDisabled$ = combineLatest([
      this.filteredCodes$,
      isDisabled$(this.form),
    ]).pipe(map(([codes, isDisabled]) => !codes.length || isDisabled));

    this.isHighCaries$ = formControlChanges$(this.form.controls.claimType).pipe(
      map((claimType) => claimType === MohClaimType.HighCaries)
    );
  }

  async submit(): Promise<void> {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    try {
      const formData = this.form.getRawValue();
      const isHighCaries = await snapshot(this.isHighCaries$);

      const codes = await snapshot(this.filteredCodes$);
      const request = await this._approvalService.buildPreApprovalRequest(
        this.data,
        formData,
        codes,
        isHighCaries
      );

      if (formData.nhiNumber !== this.data.patient.nationalHealthIndexNumber) {
        await Firestore.patchDoc(this.data.patient.ref, {
          nationalHealthIndexNumber: formData.nhiNumber,
        });
      }

      const response = await snapshot(
        httpsCallable<
          SerialisedData<MohGenerateFormRequest>,
          SerialisedData<
            DocumentReference<IMohPreApprovalFundingRequest> | undefined
          >
        >('http-moh-generateForm')(serialise(request))
      );
      if (!response) {
        throw new Error('Failed to generate MOH claim form');
      }

      const approval = await Firestore.getDoc(
        request.requestRef as DocumentReference<IMohPreApprovalFundingRequest>
      );
      await this._approvalService.signAndSubmitApprovalRequest(approval);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
    } finally {
      this.loading.set(false);
      this._dialogRef.close();
    }
  }

  getClaimTypeLabel(claimType: MohClaimType): string {
    return MOH_CLAIM_TYPE_LABEL_MAP[claimType];
  }

  claimTypeDisabled(claimType: MohClaimType): boolean {
    return !this._allClaimTypes.includes(claimType);
  }

  private _inferClaimType(): MohClaimType {
    const counts = toPairs(countBy(this._preApprovalClaimTypes)).map(
      ([type, count]) => ({
        type: type as MohClaimType,
        count,
      })
    );
    const sorted = orderBy(counts, ['count'], ['desc']);

    return first(sorted)?.type ?? MohClaimType.OHSA;
  }
}
